"use strict";
/**
 * Per-gym features — the single catalog every app reads.
 *
 * Two switches decide whether a feature runs for a gym:
 *   allowed  — set by the platform super admin (what the gym may use)
 *   enabled  — set by the gym's admin, only among allowed features
 * A feature is *active* when it is allowed, enabled, and every feature it
 * `requires` is active too.
 *
 * Adding a feature:
 *   1. Add an entry here (pick defaults deliberately — see below).
 *   2. Mark its API routes `@RequiresFeature("<key>")` and check
 *      `FeaturesService.isActive()` in any cron, listener or side effect.
 *   3. Hide its UI with the clients' `useFeature("<key>")`.
 * The super-admin and gym-admin screens list features from this catalog,
 * so nothing else needs wiring. The API's route sweep fails until every
 * new endpoint is either feature-gated or marked `@CoreFeature()`.
 *
 * Defaults apply to every gym that has no stored choice:
 *   defaultAllowed — false for features sold separately, so existing gyms
 *                    don't get them for free when they ship.
 *   defaultEnabled — false for features that need setup before they help
 *                    (an entrance screen, content to publish).
 *
 * Never reuse or rename a key: gyms' stored choices are keyed by it.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.FEATURE_DISABLED_CODE = exports.FEATURE_KEYS = exports.FEATURES = exports.FEATURE_CATEGORIES = void 0;
exports.isFeatureKey = isFeatureKey;
exports.resolveFeatures = resolveFeatures;
exports.toFeatureMap = toFeatureMap;
exports.FEATURE_CATEGORIES = {
    live: 'Live classes',
    engagement: 'Member engagement',
    members: 'Member management',
    business: 'Business',
    screens: 'Screens',
    integrations: 'Integrations',
};
exports.FEATURES = {
    // ── Live classes ──────────────────────────────────────────────────────────
    team_battles: {
        name: 'Team battles',
        description: 'Coaches split a class into 2–3 teams and the TV shows team standings live.',
        category: 'live',
        defaultAllowed: true,
        defaultEnabled: true,
    },
    interval_timer: {
        name: 'Interval timer',
        description: 'Coach-controlled Tabata / EMOM / custom work-rest timer on the class TV.',
        category: 'live',
        defaultAllowed: true,
        defaultEnabled: true,
    },
    shoutouts: {
        name: 'Shoutouts & spotlight',
        description: 'Coaches send a shoutout to an athlete or put them in the spotlight on the TV during class.',
        category: 'live',
        defaultAllowed: true,
        defaultEnabled: true,
    },
    // ── Member engagement ─────────────────────────────────────────────────────
    gym_leaderboard: {
        name: 'Gym leaderboard',
        description: 'Week, month and all-time rankings in the app and on idle TVs.',
        category: 'engagement',
        defaultAllowed: true,
        defaultEnabled: true,
    },
    badges: {
        name: 'Badges',
        description: 'Members earn badges automatically for milestones, streaks and records.',
        category: 'engagement',
        defaultAllowed: true,
        defaultEnabled: true,
    },
    challenges: {
        name: 'Challenges',
        description: 'Staff create gym challenges (classes, calories, minutes, visits) that members join.',
        category: 'engagement',
        defaultAllowed: true,
        defaultEnabled: true,
    },
    retention_pushes: {
        name: 'Reminders & progress notifications',
        description: 'Class reminders, streak nudges and the weekly summary push. Members can still mute each one.',
        category: 'engagement',
        defaultAllowed: true,
        defaultEnabled: true,
    },
    // ── Member management ─────────────────────────────────────────────────────
    member_insights: {
        name: 'Owner insights',
        description: 'At-risk and active members, attendance trends, peak times and the follow-up list.',
        category: 'members',
        defaultAllowed: true,
        defaultEnabled: true,
    },
    member_notes: {
        name: 'Coach notes & follow-ups',
        description: 'Private staff notes on a member and flags to follow up with them.',
        category: 'members',
        defaultAllowed: true,
        defaultEnabled: true,
    },
    floor_checkin: {
        name: 'Gym-floor check-in',
        description: 'Members check in at an entrance screen when they train on their own. Visits count toward streaks, boards and challenges, and power the crowd meter.',
        category: 'members',
        defaultAllowed: true,
        defaultEnabled: false,
    },
    floor_workouts: {
        name: 'Floor workout import',
        description: 'After a floor check-in, members can add the workout from Apple Health / Health Connect.',
        category: 'members',
        defaultAllowed: true,
        defaultEnabled: true,
        requires: ['floor_checkin'],
    },
    // ── Business ──────────────────────────────────────────────────────────────
    memberships: {
        name: 'Memberships & class packs',
        description: 'Plans and passes sold by staff, class credits, late-cancel policy, revenue and expiry reminders.',
        category: 'business',
        defaultAllowed: true,
        defaultEnabled: true,
    },
    // ── Screens ───────────────────────────────────────────────────────────────
    ads: {
        name: 'Gym ads',
        description: "The gym's own promotions on idle TVs and the app's Home tab. Never shown during a class.",
        category: 'screens',
        defaultAllowed: true,
        defaultEnabled: true,
    },
    // ── Integrations ──────────────────────────────────────────────────────────
    calendar_sync: {
        name: 'Calendar sync',
        description: "A private calendar feed of each member's booked classes.",
        category: 'integrations',
        defaultAllowed: true,
        defaultEnabled: true,
    },
    health_export: {
        name: 'Health app export',
        description: 'Members can save finished classes to Apple Health / Health Connect.',
        category: 'integrations',
        defaultAllowed: true,
        defaultEnabled: true,
    },
};
exports.FEATURE_KEYS = Object.keys(exports.FEATURES);
function isFeatureKey(key) {
    return Object.prototype.hasOwnProperty.call(exports.FEATURES, key);
}
/**
 * Resolve every feature for a gym from its stored choices. Pure, so the
 * API, tests and clients agree on what "active" means.
 */
function resolveFeatures(stored) {
    const base = {};
    for (const key of exports.FEATURE_KEYS) {
        const def = exports.FEATURES[key];
        const s = stored[key];
        base[key] = {
            allowed: s?.allowed ?? def.defaultAllowed,
            enabled: s?.enabled ?? def.defaultEnabled,
        };
    }
    const out = {};
    const resolving = new Set();
    const resolve = (key) => {
        if (out[key])
            return out[key];
        if (resolving.has(key))
            throw new Error(`Feature dependency cycle at "${key}"`);
        resolving.add(key);
        const def = exports.FEATURES[key];
        const blockedBy = (def.requires ?? []).filter((r) => !resolve(r).active);
        const { allowed, enabled } = base[key];
        out[key] = { key, allowed, enabled, active: allowed && enabled && blockedBy.length === 0, blockedBy };
        resolving.delete(key);
        return out[key];
    };
    exports.FEATURE_KEYS.forEach(resolve);
    return out;
}
function toFeatureMap(states) {
    const map = {};
    for (const key of exports.FEATURE_KEYS)
        map[key] = states[key].active;
    return map;
}
/** Error code the API returns (403) when a request needs an inactive feature. */
exports.FEATURE_DISABLED_CODE = 'FEATURE_DISABLED';
//# sourceMappingURL=features.js.map